import { action, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IVatData } from "./InvoiceTypes";
import { ErpType } from "src/network/User";
import { GET_VAT_DATA } from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { INVOICE_ACCOUNT_VAT_CONFIG, VAT_RATE_TYPE } from "./InvoiceEnums";
import { GetVatData, GetVatDataVariables } from "src/api/generated/GetVatData";
import { formatDateForElement } from "./Utils";
import { getLang } from "src/utils/language";

export enum LOADING_VAT_DATA_STATE {
    INIT,
    LOADING
}

export class AccountingVatStore {
    rootStore: RootStore;

    erpType: ErpType = ErpType.IT2;

    vatData: IVatData[] = [];
    loadingVatDataState: LOADING_VAT_DATA_STATE = LOADING_VAT_DATA_STATE.INIT;
    // GET_VAT_DATA has no realestate variable - the result is the same for every accounting row,
    // so one request serves all of them (shared while in flight, kept afterwards). Not observable.
    private vatDataRequest: Promise<IVatData[]> | undefined = undefined;
    private vatDataLanguage: string | undefined = undefined; // The language of the last GET_VAT_DATA request. If it changes, a new request is made.

    // This is an array because every Accounting Row has its own vat code / rate value

    currentVatCodeSearchQuery: string[] = [];
    selectedVatCodeQueryString: string[] = [];
    selectedVatCodeVatRateType: VAT_RATE_TYPE[] = [];

    currentVatRateSearchQuery: string[] = [];
    selectedVatRateQueryString: string[] = [];

    triggerFocusVatRateInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };
    triggerFocusNextAccountingField: { isActive: boolean; index: number } = { isActive: false, index: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            vatData: observable,
            currentVatCodeSearchQuery: observable,
            selectedVatCodeQueryString: observable,
            currentVatRateSearchQuery: observable,
            selectedVatRateQueryString: observable,
            loadingVatDataState: observable,
            triggerFocusVatRateInput: observable,
            triggerFocusNextAccountingField: observable,
            selectedVatCodeVatRateType: observable,

            init: action,
            reset: action,
            removeAccountingRow: action,
            loadVatData: action,
            setLoadingVatDataState: action,
            setCurrentVatCodeSearchQuery: action,
            setSelectedVatCodeQueryString: action,
            setCurrentVatRateSearchQuery: action,
            setSelectedVatRateQueryString: action,
            setSelectedVatCodeVatRateType: action,
            selectableVatCodeData: action,
            selectableVatRateData: action,
            setVatData: action,
            setTriggerFocusOnVatRateInput: action,
            setTriggerFocusOnNextAccountingField: action
        });
    }

    init = () => {};

    reset = () => {
        this.currentVatCodeSearchQuery = [];
        this.selectedVatCodeQueryString = [];
        this.selectedVatCodeVatRateType = [];

        this.currentVatRateSearchQuery = [];
        this.selectedVatRateQueryString = [];
    };

    removeAccountingRow = (accountingRowIndex: number) => {
        this.currentVatCodeSearchQuery.splice(accountingRowIndex, 1);
        this.selectedVatCodeQueryString.splice(accountingRowIndex, 1);
        this.selectedVatCodeVatRateType.splice(accountingRowIndex, 1);

        this.currentVatRateSearchQuery.splice(accountingRowIndex, 1);
        this.selectedVatRateQueryString.splice(accountingRowIndex, 1);
    };

    loadVatData = async (accountingRowIndex: number) => {
        this.setLoadingVatDataState(LOADING_VAT_DATA_STATE.LOADING);

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const realestateId = currentInvoice?.getAccountingRealestate(accountingRowIndex)?.id;

        // Vat data is only relevant once the row has a realestate (kept from the previous behaviour).
        if (realestateId) {
            try {
                // Loads once; every further row (and every concurrent caller) awaits the same promise.
                await this.fetchVatData();
            } finally {
                this.setLoadingVatDataState(LOADING_VAT_DATA_STATE.INIT);
            }
            return;
        }

        this.setLoadingVatDataState(LOADING_VAT_DATA_STATE.INIT);
    };

    private fetchVatData = (): Promise<IVatData[]> => {
        const language = getLang();
        if (this.vatDataRequest && this.vatDataLanguage === language) {
            return this.vatDataRequest;
        }

        this.vatDataLanguage = language;

        const request = apolloClientInstance
            .query<GetVatData, GetVatDataVariables>({
                query: GET_VAT_DATA,
                variables: {
                    language
                }
            })
            .then(({ data }) => {
                const vatData: IVatData[] = data.vatdata.map((vatData) => {
                    return {
                        realestateId: vatData.realestateid !== null ? vatData.realestateid : null,
                        language: vatData.vatrate_language ?? undefined,
                        rateId: vatData.vatrate_id,
                        rate: String(vatData.rate),
                        rateName: vatData.vatrate_resolved ?? undefined,
                        codeId: vatData.vatcode_id,
                        codeName: vatData.vatcodename ?? undefined,
                        codeShort: vatData.vatcodeshort ?? undefined,
                        validFrom: vatData.validfrom,
                        validTo: vatData.validto,
                        vatRateType: vatData.vatratetype ?? undefined,
                        proportionally: vatData.proportionally === true
                    };
                });

                if (this.vatDataLanguage === language) {
                    this.setVatData(vatData);
                }

                return vatData;
            })
            .catch((error) => {
                // Do not cache failures - the next caller gets a fresh attempt.
                this.vatDataRequest = undefined;
                throw error;
            });

        this.vatDataRequest = request;

        return request;
    };

    // Getters
    getFormattedVatCodeFieldValue = (vatData: IVatData): string => {
        return `${vatData.codeName} [${vatData.codeShort}]`;
    };

    getFormattedVatRateFieldValue = (vatData: IVatData): string => {
        return vatData.rate ?? "";
    };

    getCurrentVatCodeSearchQuery(accountingRowIndex: number) {
        return this.currentVatCodeSearchQuery[accountingRowIndex];
    }

    getSelectedVatCodeQueryString(accountingRowIndex: number) {
        return this.selectedVatCodeQueryString[accountingRowIndex];
    }

    getCurrentVatRateSearchQuery(accountingRowIndex: number) {
        return this.currentVatRateSearchQuery[accountingRowIndex];
    }

    getSelectedVatRateQueryString(accountingRowIndex: number) {
        return this.selectedVatRateQueryString[accountingRowIndex];
    }

    getSelectedVatCodeVatRateType(accountingRowIndex: number): VAT_RATE_TYPE | undefined {
        return this.selectedVatCodeVatRateType[accountingRowIndex];
    }

    isProportionalVatCode(vatCodeId: string | undefined): boolean {
        if (!vatCodeId) {
            return false;
        }

        return this.vatData.some((vatData) => vatData.codeId === vatCodeId && vatData.proportionally);
    }

    getCurrentlyValidVatRateDataByVatCodeId(vatCodeId: string): IVatData | undefined {
        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        let currentlyValidVatRateData: IVatData | undefined = undefined;

        if (currentInvoice) {
            const vatRateData = this.vatData.filter((vatData) => {
                return vatData.codeId === vatCodeId;
            });

            if (vatRateData) {
                const currentDate = formatDateForElement(new Date());

                vatRateData.forEach((vatData) => {
                    if (vatData.validTo) {
                        if (vatData.validFrom <= currentDate && vatData.validTo >= currentDate) {
                            currentlyValidVatRateData = vatData;
                        }
                    } else {
                        if (vatData.validFrom <= currentDate && vatData.validTo === null) {
                            currentlyValidVatRateData = vatData;
                        }
                    }
                });
            }
        }

        return currentlyValidVatRateData;
    }

    getVatDataByVatRate(accountingRowIndex: number, vatRate: string): IVatData | undefined {
        let vatData = undefined;

        this.selectableVatRateData(accountingRowIndex).forEach((selectableVatData) => {
            if (selectableVatData.rate === vatRate) {
                vatData = selectableVatData;
            }
        });

        return vatData;
    }

    isValidVatRateForSelectedVatCode(accountingRowIndex: number, vatRate: string): boolean {
        let isValidVatRate = false;

        this.selectableVatRateData(accountingRowIndex).forEach((vatData) => {
            if (vatData.rate === vatRate) {
                isValidVatRate = true;
            }
        });

        return isValidVatRate;
    }

    // Setters
    /*  This function triggers the focus on the next field after selecting an account
        For IT2 this will be the CostCenter field in the current accounting row
        For RIMO this will be the Booking Text field in the current accounting row
    */
    setTriggerFocusOnVatRateInput(triggerFocusNextInput: boolean, accountingRowIndex: number) {
        this.triggerFocusVatRateInput = {
            isActive: triggerFocusNextInput,
            index: accountingRowIndex
        };
    }

    setTriggerFocusOnNextAccountingField(triggerFocusNextInput: boolean, accountingRowIndex: number) {
        this.triggerFocusVatRateInput = {
            isActive: triggerFocusNextInput,
            index: accountingRowIndex
        };
    }

    setLoadingVatDataState(loadingAccountDataState: LOADING_VAT_DATA_STATE) {
        this.loadingVatDataState = loadingAccountDataState;
    }

    setVatData(data: IVatData[]) {
        this.vatData = data;
    }

    setCurrentVatCodeSearchQuery(currentAccountSearchQuery: string, accountingRowIndex: number) {
        this.currentVatCodeSearchQuery[accountingRowIndex] = currentAccountSearchQuery;
    }

    setSelectedVatCodeQueryString(selectedAccountQueryString: string, accountingRowIndex: number) {
        this.selectedVatCodeQueryString[accountingRowIndex] = selectedAccountQueryString;
    }

    setCurrentVatRateSearchQuery(currentAccountSearchQuery: string, accountingRowIndex: number) {
        this.currentVatRateSearchQuery[accountingRowIndex] = currentAccountSearchQuery;
    }

    setSelectedVatRateQueryString(selectedAccountQueryString: string, accountingRowIndex: number) {
        this.selectedVatRateQueryString[accountingRowIndex] = selectedAccountQueryString;
    }

    setSelectedVatCodeVatRateType(vatRateType: VAT_RATE_TYPE, accountingRowIndex: number) {
        this.selectedVatCodeVatRateType[accountingRowIndex] = vatRateType;
    }

    selectableVatCodeData(accountingRowIndex: number): IVatData[] {
        let selectableVatData: IVatData[] = [];

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const account = currentInvoice?.getAccountingAccount(accountingRowIndex);

        if (account && account.vatConfig !== INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT) {
            const realestateId = currentInvoice?.getAccountingRealestate(accountingRowIndex)?.id;

            selectableVatData = this.vatData
                .filter((vatData) => {
                    // Selected realestate of accounting entry must match the realestate of the vat data (or be null)
                    return vatData.realestateId === realestateId || vatData.realestateId === null;
                })
                .filter((vatData) => {
                    // Current date must be valid
                    const currentDate = formatDateForElement(new Date());

                    if (vatData.validTo) {
                        return vatData.validFrom <= currentDate && vatData.validTo >= currentDate;
                    } else {
                        return vatData.validFrom <= currentDate && vatData.validTo === null;
                    }
                });
        }

        let currentAccountSearchQuery = this.currentVatCodeSearchQuery[accountingRowIndex] ?? "";
        let selectedRealestateQueryString = this.selectedVatCodeQueryString[accountingRowIndex] ?? "";

        if (currentAccountSearchQuery === selectedRealestateQueryString) {
            return selectableVatData;
        }

        if (currentAccountSearchQuery === "") {
            return selectableVatData;
        }

        const filteredAccountData = selectableVatData.filter((vatData) => {
            const vatCodeName = this.getFormattedVatCodeFieldValue(vatData);

            return vatCodeName.toLowerCase().includes(currentAccountSearchQuery.toLowerCase());
        });

        return filteredAccountData;
    }

    selectableVatRateData(accountingRowIndex: number): IVatData[] {
        let selectableVatRateData: IVatData[] = [];

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const currentlySelectedVatCodeId = currentInvoice?.getAccountingVatCodeId(accountingRowIndex);

        if (currentlySelectedVatCodeId) {
            this.vatData.forEach((vatData) => {
                if (vatData.codeId && vatData.codeId === currentlySelectedVatCodeId) {
                    selectableVatRateData.push(vatData);
                }
            });
        }

        // Sort by validFrom (date) in ascending order
        selectableVatRateData.sort((a, b) => {
            if (a.validFrom !== undefined && b.validFrom !== undefined) {
                return new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime();
            } else {
                return 0;
            }
        });

        // Find the index of the rate that is currently valid
        const indexOfCurrentlyValidVatRateData = selectableVatRateData.findIndex((vatData) => {
            const currentDate = formatDateForElement(new Date());
            if (vatData.validTo) {
                return vatData.validFrom <= currentDate && vatData.validTo >= currentDate;
            } else {
                return vatData.validFrom <= currentDate && vatData.validTo === null;
            }
        });

        if (indexOfCurrentlyValidVatRateData !== -1) {
            // only select the rate that is currently valid + the rate that was valid before and the rate that will be valid after
            const sliceStart = indexOfCurrentlyValidVatRateData - 1 < 0 ? 0 : indexOfCurrentlyValidVatRateData - 1;
            const sliceEnd =
                indexOfCurrentlyValidVatRateData + 2 > selectableVatRateData.length
                    ? selectableVatRateData.length
                    : indexOfCurrentlyValidVatRateData + 2;

            selectableVatRateData = selectableVatRateData.slice(sliceStart, sliceEnd);
        }

        // Sort by vatRate
        selectableVatRateData.sort((a, b) => {
            if (a.rate !== undefined && b.rate !== undefined) {
                return Number(a.rate) - Number(b.rate);
            } else {
                return 0;
            }
        });

        return selectableVatRateData;
    }
}
